import { createSelector } from '@reduxjs/toolkit';
import { getGroupsByDeviceIdByPropsSelector } from '../../groups/selectors';
import isComposableDevice from '../../composableDevice/util/isComposableDevice';
import { RootState } from '../../store.model';
import { IncidentAPIResponseMVP } from '../api/Incident.model';
import { getMachinesTypeSelector } from '../../composableDevice/selectors/composableDeviceSelectors';
import { ComposableDeviceUserInterfaces } from '../../composableDevice/ComposableDevice.model';
import { NormalizedGroup } from '../../groups/api/GroupAdapter';
import { MachineType } from '../../stoerkID/StoerkIdAPI.model';

const getComposableDeviceGroupsByIncidentIdSelector = createSelector(
  [getGroupsByDeviceIdByPropsSelector],
  (result) => {
    return result.filter(isComposableDevice);
  }
);

export const getComposableDeviceGroupsByIncidentDeviceIdSelector =
  createSelector(
    [(state: RootState) => state, (_rootState, props) => props.devicesIds],
    (state, devicesIds) => {
      return devicesIds
        .map((deviceId: string) =>
          getComposableDeviceGroupsByIncidentIdSelector(state, { deviceId })
        )
        .flat();
    }
  );

export const getComposableDeviceByControlUnitAndDeviceId = createSelector(
  [(state: RootState) => state, (_state: RootState, props) => props],
  (state, incidentsData) =>
    ({
      controlUnitId,
      deviceId,
    }: {
      controlUnitId: number | string;
      deviceId: string;
    }) =>
      getComposableDeviceGroup(state, {
        incidentsData,
        controlUnitId,
        deviceId,
      })
);

export const getComposableDeviceGroup = createSelector(
  [
    (_state, props) => props.controlUnitId,
    (_state, props) => props.deviceId,
    (_state: RootState, props) =>
      getComposableDeviceGroupsByIncidentDeviceIdSelector(_state, {
        devicesIds: props.incidentsData?.map(
          (incident: IncidentAPIResponseMVP) => incident.deviceId
        ),
      }),
    getMachinesTypeSelector,
  ],
  (controlUnitId, deviceId, groups, machinesTypes) => {
    const groupsWithDevice = groups.filter((group: NormalizedGroup) =>
      group?.devices?.includes(deviceId)
    );
    return groupsWithDevice.find((group: NormalizedGroup) => {
      const machineType = machinesTypes.find(
        (type: MachineType) => type.id === group.composableDevice?.machineTypeId
      );
      let heated, refrigerated, controlUnits;
      switch (machineType?.userInterface) {
        case null:
        case undefined:
          return null;
        case ComposableDeviceUserInterfaces.SCC_MERCHANDISE_V2:
          return group.composableDevice?.channels?.some(
            (channel) => channel.controlUnitId === `${controlUnitId}`
          );
        case ComposableDeviceUserInterfaces.SCC_MERCHANDISE:
          heated = group.composableDevice?.labels?.cus_heated?.split(',');
          refrigerated =
            group.composableDevice?.labels?.cus_refrigerated?.split(',');
          controlUnits = [
            ...(heated?.map((cu) => cu.split('/')[1] || '') ?? []),
            ...(refrigerated?.map((cu) => cu.split('/')[1] || '') ?? []),
          ];
          return controlUnits.includes(`${controlUnitId}`);
        case ComposableDeviceUserInterfaces.FLEXESERVE_AIRCONNECT:
        case ComposableDeviceUserInterfaces.FLEXESERVE_UI_HUB:
        case ComposableDeviceUserInterfaces.FLEXESERVE_UI_SHELLY:
          return group.composableDevice?.controlUnits?.some(
            (controlUnit) => controlUnit.controlUnitId === `${controlUnitId}`
          );
        default:
          return null;
      }
    });
  }
);
